import type {DataRouterFileOverviewPayload, TreePayload} from '@github-ui/code-view-types'
import type {ReactNode} from 'react'

import {CodeViewBannersProvider} from '@github-ui/code-view-shared/contexts/CodeViewBannersContext'
import {CopilotSettingsProvider} from '@github-ui/code-view-shared/contexts/CopilotSettingsContext'
import {CurrentTreeProvider} from '@github-ui/code-view-shared/contexts/CurrentTreeContext'
import {EditPermissionsProvider} from '@github-ui/code-view-shared/contexts/EditPermissionsContext'
import {RefInfoProvider} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import {PathProvider} from '@github-ui/code-view-shared/contexts/PathContext'
import {ActionProvider} from '@github-ui/code-view-shared/contexts/ActionContext'
import {UserNameDisplayContextProvider} from '@github-ui/profile-reference/UserNameDisplayContext'

import OverviewContent from './components/OverviewContent'
import {useCanonicalObject} from '@github-ui/code-view-shared/hooks/use-canonical-object'
import {useWebVitals} from '@github-ui/use-web-vitals'
import {sendCustomMetric} from '@github-ui/stats'

export function ReposOverview({
  payload,
  className,
  tree,
  readmeHeaderSlot,
  noReadmeSlot,
}: {
  payload: DataRouterFileOverviewPayload
  className?: string
  tree: TreePayload
  /** Ask Copilot README-header entry, rendered on the right of the README tab bar. */
  readmeHeaderSlot?: ReactNode
  /** Copilot card shown when no README or community files exist. */
  noReadmeSlot?: ReactNode
}) {
  const refInfo = useCanonicalObject(payload.refInfo)
  useWebVitals('hpc', event => {
    if (!event.detail.hpc) return
    sendCustomMetric({
      name: 'CODE_VIEW_REPO_OVERVIEW_HPC',
      value: event.detail.hpc,
      tags: {
        app: 'code-view',
        soft: String(event.detail.soft),
        ssr: String(event.detail.ssr),
        mechanism: String(event.detail.mechanism),
      },
    })
  })

  useWebVitals('inp', event => {
    if (!event.detail.inp) return
    sendCustomMetric({
      name: 'CODE_VIEW_REPO_OVERVIEW_INP',
      value: event.detail.inp,
      tags: {
        app: 'code-view',
        inpInteractionType: event.detail.inpInteractionType || 'unknown',
        inpEventType: event.detail.inpEventType || 'unknown',
        inpBottleneck: event.detail.inpBottleneck || 'unknown',
      },
    })
  })

  return (
    <PathProvider path={payload.path}>
      <ActionProvider action="tree">
        <CopilotSettingsProvider copilotAccessAllowed={payload.copilotAccessAllowed ?? false}>
          <RefInfoProvider refInfo={refInfo}>
            <EditPermissionsProvider
              canEdit={refInfo.canEdit}
              canEditOnDefaultBranch={refInfo.canEditOnDefaultBranch}
              fileExistsOnDefault={refInfo.fileExistsOnDefault}
            >
              <CodeViewBannersProvider>
                <CurrentTreeProvider payload={tree}>
                  <UserNameDisplayContextProvider value={payload.userNameDisplayConfiguration}>
                    <OverviewContent
                      payload={payload}
                      className={className}
                      readmeHeaderSlot={readmeHeaderSlot}
                      noReadmeSlot={noReadmeSlot}
                    />
                  </UserNameDisplayContextProvider>
                </CurrentTreeProvider>
              </CodeViewBannersProvider>
            </EditPermissionsProvider>
          </RefInfoProvider>
        </CopilotSettingsProvider>
      </ActionProvider>
    </PathProvider>
  )
}
